import { Box, Combobox, Text, Textarea, TextInput, useCombobox } from '@mantine/core';
import { useRef, useState } from 'react';
import {
  insertPlaceholder,
  placeholderKind,
  placeholderOptions,
  placeholderQuery,
  placeholderTokens,
} from './placeholders';
import classes from './workspace.module.css';

const TIPS = {
  args: 'Argument the agent fills',
  shopper: 'The shopper',
  unknown: 'Not a declared argument or shopper field',
};

export const PlaceholderTokens = ({ text, argNames, testValues = {} }) => {
  const tokens = placeholderTokens(text);
  if (!tokens.some((token) => token.name)) return null;

  return (
    <Box className={classes.tokens} data-testid="placeholder-tokens">
      {tokens.map((token, index) => {
        if (!token.name)
          return (
            <Text span inherit key={index}>
              {token.text}
            </Text>
          );
        const kind = placeholderKind(token.name, argNames);
        const value = testValues[token.name];
        const tip = kind === 'unknown' ? TIPS.unknown : `${TIPS[kind]} · test value: ${value || '(empty)'}`;
        return (
          <Text span inherit key={index} className={`${classes.ph} ${classes[kind]}`} title={tip} data-kind={kind}>
            {`{{${token.name}}}`}
          </Text>
        );
      })}
    </Box>
  );
};

// A text box that offers the known placeholders after `{{` and shows them coloured by kind below.
const PlaceholderInput = ({ value, onChange, argNames, testValues, multiline = false, ...props }) => {
  const combobox = useCombobox();
  const ref = useRef(null);
  const [query, setQuery] = useState(null);

  const options = query
    ? placeholderOptions(argNames).filter((name) => name.toLowerCase().includes(query.typed.toLowerCase()))
    : [];

  const change = (event) => {
    const next = event.target.value;
    onChange(next);
    const caret = event.target.selectionStart ?? next.length;
    const found = placeholderQuery(next, caret);
    setQuery(found && { ...found, caret });
    if (found) combobox.openDropdown();
    else combobox.closeDropdown();
  };

  const pick = (name) => {
    const next = insertPlaceholder(value, query.caret, query, name);
    onChange(next.text);
    setQuery(null);
    combobox.closeDropdown();
    // The value lands on the next render, so the caret is placed after it.
    requestAnimationFrame(() => ref.current?.setSelectionRange?.(next.caret, next.caret));
  };

  const Input = multiline ? Textarea : TextInput;

  return (
    <Box miw={0} style={{ flex: 1 }}>
      <Combobox store={combobox} onOptionSubmit={pick} position="bottom-start" withinPortal={false}>
        <Combobox.Target>
          <Input
            ref={ref}
            value={value}
            onChange={change}
            onBlur={() => combobox.closeDropdown()}
            spellCheck={false}
            styles={{ input: { fontFamily: 'var(--mantine-font-family-monospace)', fontSize: 12 } }}
            {...props}
          />
        </Combobox.Target>
        <Combobox.Dropdown hidden={!options.length}>
          <Combobox.Options>
            {options.map((name) => (
              <Combobox.Option key={name} value={name}>
                <Text span inherit className={`${classes.ph} ${classes[placeholderKind(name, argNames)]}`}>
                  {`{{${name}}}`}
                </Text>
              </Combobox.Option>
            ))}
          </Combobox.Options>
        </Combobox.Dropdown>
      </Combobox>
      <PlaceholderTokens text={value} argNames={argNames} testValues={testValues} />
    </Box>
  );
};

export default PlaceholderInput;
